import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { LastPathPersistence } from '@components/last-path-persistence';
import { Layout } from '@components/layout/src/layout';
import { ErrorBoundary } from '@containers/error-boundary';
import { AuthProvider } from '@contexts/auth/auth-context';
import { CompanyAuthContextProvider } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubProvider } from '@contexts/companyClub/companyClub-context';
import { DarkModeProvider } from '@contexts/darkMode/darkMode-context';
import { FeatureFlagProvider } from '@contexts/featureFlag/featureFlag-context';
import { FloorplanProvider } from '@contexts/floorplan/floorplan-context';
import { GoogleTagManagerProvider } from '@contexts/google-tag-manager/google-tag-manager-context';
import { LayoutProvider } from '@contexts/layout/layout-context';
import { ModalProvider } from '@contexts/modal/modal-context';
import { EventNavigationProvider } from '@contexts/navigation/event-navigation-context';
import { NavigationProvider } from '@contexts/navigation/navigation-context';
import { ThemeProvider } from '@contexts/theme/theme-context';
import { ToastProvider } from '@contexts/toast/toast-context';
import { UserProvider } from '@contexts/user/user-context';
import { WebviewBridgeProvider } from '@contexts/webview-bridge';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import moment from 'moment-timezone';

import { AppRoutes } from './appRoutes';
import { QueryClientComponent } from './query-client';

const {
  VITE_VERSION,
  VITE_NAME,
  VITE_SENTRY_ENABLED,
  VITE_SENTRY_ENVIRONMENT,
  VITE_SENTRY_REPLAY_ERROR_SESSION_RATE: replayErrorRate,
  VITE_SENTRY_REPLAY_SESSION_RATE: replaySessionRate,
} = import.meta.env;

if (VITE_SENTRY_ENABLED === 'true') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
      }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.httpClientIntegration(),
    ],
    sendDefaultPii: true,
    replaysSessionSampleRate: Number(replaySessionRate),
    replaysOnErrorSampleRate: Number(replayErrorRate),
    release: `${VITE_NAME}@${VITE_VERSION}`,
    environment: VITE_SENTRY_ENVIRONMENT,
  });
}

const Application = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  useEffect(() => {
    moment.locale(locale);
  }, [locale]);

  return (
    <ThemeProvider>
      <QueryClientComponent>
        <ErrorBoundary>
          <GoogleTagManagerProvider>
            <ToastProvider>
              <WebviewBridgeProvider>
                <BrowserRouter basename={import.meta.env.BASE_URL}>
                  <UserProvider>
                    <FeatureFlagProvider>
                      <AuthProvider>
                        <CompanyAuthContextProvider>
                          <LayoutProvider>
                            <CompanyClubProvider>
                              <NavigationProvider>
                                <EventNavigationProvider>
                                  <FloorplanProvider>
                                    <DarkModeProvider>
                                      <ModalProvider>
                                        <LastPathPersistence />
                                        <Layout>
                                          <AppRoutes />
                                        </Layout>
                                      </ModalProvider>
                                    </DarkModeProvider>
                                  </FloorplanProvider>
                                </EventNavigationProvider>
                              </NavigationProvider>
                            </CompanyClubProvider>
                          </LayoutProvider>
                        </CompanyAuthContextProvider>
                      </AuthProvider>
                    </FeatureFlagProvider>
                  </UserProvider>
                </BrowserRouter>
                <ReactQueryDevtools initialIsOpen={false} />
              </WebviewBridgeProvider>
            </ToastProvider>
          </GoogleTagManagerProvider>
        </ErrorBoundary>
      </QueryClientComponent>
    </ThemeProvider>
  );
};

export const App = Sentry.withProfiler(Application);
